import groundcover from '@groundcover/browser';
import type {Level, LogEvent} from 'pino';

const SENSITIVE_KEYS = new Set([
  `accesstoken`,
  `access_token`,
  `idtoken`,
  `id_token`,
  `refreshtoken`,
  `refresh_token`,
  `authorization`,
  `cookie`,
  `password`,
  `secret`,
  `apikey`,
  `api_key`,
]);

const cleanUp = (value: unknown): unknown => {
  if (value instanceof Error) return {name: value.name, message: value.message, stack: value.stack};
  if (Array.isArray(value)) return value.map(cleanUp);
  if (value && typeof value === `object`) {
    return Object.fromEntries(
      Object.entries(value).map(([key, v]) => [key, SENSITIVE_KEYS.has(key.toLowerCase()) ? `[REDACTED]` : cleanUp(v)]),
    );
  }
  return value;
};

// pino browser `transmit` sink: forwards warn+ logs to Groundcover with the log object as attributes.
export const sendToGroundcover = (level: Level, {messages, bindings}: LogEvent) => {
  try {
    // Console-style `logger.error('text', context)` is swapped into pino's `(context, 'text')` order.
    const [first, second] = typeof messages[0] === `string` ? [messages[1], messages[0]] : messages;
    const fields: Record<string, unknown> = first && typeof first === `object` ? first : {};

    // `msg` is pino's own key (its browser err serializer uses it too): promote it to the text when no message was given.
    const {msg, ...rest} = {...Object.assign({}, ...bindings), ...fields};
    const err = (rest.err ?? rest.error) as {msg?: unknown; message?: unknown} | undefined;
    const errText = err?.msg ?? err?.message;
    const message = second ?? msg ?? errText ?? ``;

    // `logger` tells logger-originated entries apart from the SDK's own console capture; `source` is Groundcover's own field.
    groundcover.logger[level === `warn` ? `warn` : `error`](
      String(message),
      cleanUp({...rest, logger: `pino`}) as Record<string, unknown>,
    );
  } catch {
    // Best-effort: a circular payload or SDK failure must not throw out of the caller's logger.error.
  }
};
